import { computed, inject, toRaw } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

import {
  GetAuthoringStarted,
  GetDisplayReferencesOnDocumentLevel,
  GetExpandedFilters,
  GetFootnotes,
  GetSelectedValues,
  GetTacticContextFilters,
} from '~/constants/getters';
import {
  ResetTacticContextFilter,
  SetDisplayReferencesOnDocumentLevel,
  SetExpandedFilters,
  SetFootnotes,
  SetIsAuthoringStarted,
  SetTacticContextFilter,
  SetSelectedValues,
  SetConversationScope,
} from '~/constants/actions';
import { Routes } from '~/constants/routes';
import { DEFAULT_COLLECTION, METADATA } from '~/constants/conversationModels';

const getMessageCollectionId = (message) => {
  const collectionId =
    message?.collectionId?.toString() ?? message?.collection_id?.collectionId?.toString();

  if (!collectionId || collectionId === DEFAULT_COLLECTION.collectionId) {
    return undefined;
  }

  return collectionId;
};

import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import { mapTacticToTacticType } from '~/utils/editor-utils';
import { sortByTimestamp } from '~/utils/date-utils';
import useFormFilters from '~/composables/useFormFilters';

export default () => {
  const store = useStore();
  const arcaneApiGateway = inject('arcaneApiGateway');
  const route = useRoute();
  const { setFiltersObject } = useFormFilters();

  const isAuthoringStarted = computed(() => store.getters[`authoringStore/${GetAuthoringStarted}`]);
  const expandedFilters = computed(() => store.getters[`authoringStore/${GetExpandedFilters}`]);
  const tacticContextFilters = computed(() => store.getters[`authoringStore/${GetTacticContextFilters}`]);
  const isAuthoringPage = computed(() => route.name === Routes.Authoring.name);
  const footnotes = computed(() => store.getters[`authoringStore/${GetFootnotes}`]);
  const displayReferencesOnDocumentLevel = computed(
    () => store.getters[`authoringStore/${GetDisplayReferencesOnDocumentLevel}`],
  );
  const selectedValues = computed(() => store.getters[`authoringStore/${GetSelectedValues}`]);
  const isAuthoringInputDisabled = computed(() => !selectedValues.value?.collection);

  const setIsAuthoringStarted = (data) => store.dispatch(`authoringStore/${SetIsAuthoringStarted}`, data);
  const setExpandedFilters = (data) => store.dispatch(`authoringStore/${SetExpandedFilters}`, data);
  const setTacticContextFilters = (data) => store.dispatch(`authoringStore/${SetTacticContextFilter}`, data);
  const resetTacticContextFilters = (data) => store.dispatch(`authoringStore/${ResetTacticContextFilter}`, data);
  const setFootnotes = (data) => store.dispatch(`authoringStore/${SetFootnotes}`, data);
  const setDisplayReferencesOnDocumentLevel = (data) =>
    store.dispatch(`authoringStore/${SetDisplayReferencesOnDocumentLevel}`, data);

  const getTacticTypeAndTacticsByRegion = async (regionId) => {
    const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.tacticContextTypes(regionId));
    return data;
  };

  const getTacticContextById = async (id) => {
    const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.getTacticContextById(id));

    if (data.length) {
      return data[0];
    }

    return null;
  };

  const getRegions = async () => {
    const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.regions);

    return data;
  };

  const getTacticContexts = async (id, region) => {
    await arcaneApiGateway.get(apiGatewayEndpoints.tacticContexts.tacticContexts, {
      id,
      region,
    });
  };

  const handleAuthoringMetadata = async (conversation, currentCollections) => {
    const { messages = [] } = conversation;
    const lastMessageOfConversation = sortByTimestamp([...(messages || [])])[0];

    if (lastMessageOfConversation) {
      await Promise.all(
        (lastMessageOfConversation.tactic_context || []).map(async (item) => {
          try {
            const tacticById = await getTacticContextById(item.id);

            const tacticContextTypes = await getTacticTypeAndTacticsByRegion(tacticById.regionId);

            const selectedTacticContextType = tacticContextTypes.find((tact) =>
              tact.tacticContexts.find((it) => it.id === item.id),
            );
            const selectedTacticContextTypeWithDesciption = mapTacticToTacticType([selectedTacticContextType]);

            setTacticContextFilters({
              contextTypeId: tacticById.contextTypeId,
              tacticContext: {
                ...tacticById,
                regionDescriptionName:
                  selectedTacticContextTypeWithDesciption[tacticById.contextTypeId]?.[0]?.regionDescriptionName,
              },
            });
            return { [tacticById.contextTypeId]: tacticById };
          } catch (error) {
            console.error('Error fetching tactic context ', error);
            return null;
          }
        }),
      );

      const conversationCollectionId = getMessageCollectionId(lastMessageOfConversation);
      const currentCollection = conversationCollectionId
        ? currentCollections.find(
            (collection) =>
              toRaw(collection).collectionId.toString() === conversationCollectionId ||
              toRaw(collection).parentId?.toString() === conversationCollectionId, // if sharedCollection we'll check on parentId
          )
        : undefined;

      if (currentCollection) {
        store.dispatch(`conversationStore/${SetConversationScope}`, { collection: currentCollection });
        await store.dispatch(`authoringStore/${SetSelectedValues}`, {
          id: 'collection',
          value: toRaw(currentCollection).collectionId.toString(),
        });
      }

      // make it backward compatible with old metadata structure where we had only "filters"
      const allFilters = [
        ...(lastMessageOfConversation?.filters || []),
        ...(lastMessageOfConversation?.media_filters || []),
      ];

      if (allFilters?.find((item) => item.key === METADATA.product_name || item.key === METADATA.country)) {
        const productAndCountryFilters = allFilters.reduce((acc, item) => {
          if (item.key === METADATA.product_name || item.key === METADATA.country) {
            acc[item.key] = [...item.value];
          }
          return acc;
        }, {});

        setFiltersObject({ document: productAndCountryFilters });
      } else {
        setFiltersObject({ document: {} });
      }
      setExpandedFilters(false);
      setIsAuthoringStarted(true);
    }
  };

  const getActivatorUrl = async (id) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.authoredDocuments.getActivatorUrl(id));
      return { url: data.redirectUrl, statusCode: 200 };
    } catch (error) {
      return { url: null, statusCode: error?.response?.status ?? null };
    }
  };

  const publishToEwizard = async (id) => {
    try {
      await arcaneApiGateway.post(apiGatewayEndpoints.authoredDocuments.publishToEwizard(id));
      return { success: true, statusCode: 200 };
    } catch (error) {
      return { success: false, statusCode: error?.response?.status ?? null };
    }
  };

  return {
    isAuthoringStarted,
    isAuthoringInputDisabled,
    isAuthoringPage,
    selectedValues,
    expandedFilters,
    tacticContextFilters,
    footnotes,
    displayReferencesOnDocumentLevel,
    setExpandedFilters,
    setIsAuthoringStarted,
    setTacticContextFilters,
    getTacticTypeAndTacticsByRegion,
    getRegions,
    getTacticContexts,
    resetTacticContextFilters,
    getTacticContextById,
    setFootnotes,
    setDisplayReferencesOnDocumentLevel,
    handleAuthoringMetadata,
    getActivatorUrl,
    publishToEwizard,
  };
};
